// Read the ZIP index first; decompress only selected SCP entries, one at a time.
// Supports single-volume ZIP/ZIP64 (stored/deflate); encrypted archives are rejected.
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const requireValue = (condition, message) => {
  if (!condition) throw new Error(message);
};
function uint64(view, offset) {
  const value = view.getUint32(offset, true) + view.getUint32(offset + 4, true) * 4294967296;
  requireValue(Number.isSafeInteger(value), 'ZIP64 value exceeds supported precision');
  return value;
}
export function crc32(bytes) {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
  }
  return (crc ^ 0xffffffff) >>> 0;
}
function checkAbort(signal) {
  if (signal?.aborted) throw new Error('Import cancelled');
}
async function extractEntry(archive, entry, signal) {
  checkAbort(signal);
  requireValue(!(entry.flags & 1), 'Password-protected ZIP entries are not supported');
  requireValue(entry.method === 0 || entry.method === 8, 'Unsupported ZIP compression method');
  requireValue(entry.size <= MAX_FILE_BYTES, 'SCP file exceeds the 2 MiB upload limit');
  requireValue(entry.offset + 30 <= archive.size, 'Truncated ZIP local header');
  const header = new DataView(await archive.slice(entry.offset, entry.offset + 30).arrayBuffer());
  requireValue(header.getUint32(0, true) === 0x04034b50, 'Invalid ZIP local header');
  requireValue(
    header.getUint16(6, true) === entry.flags && header.getUint16(8, true) === entry.method,
    'ZIP headers disagree'
  );
  const start = entry.offset + 30 + header.getUint16(26, true) + header.getUint16(28, true);
  requireValue(start + entry.compressedSize <= entry.directoryOffset, 'Truncated ZIP entry');
  const compressed = archive.slice(start, start + entry.compressedSize);
  let stream = compressed.stream();
  if (entry.method === 8) {
    let decompressor;
    try {
      decompressor = new DecompressionStream('deflate-raw');
    } catch {
      throw new Error(
        'This browser cannot decompress ZIP files. Extract the ZIP and import its folder instead.'
      );
    }
    stream = stream.pipeThrough(decompressor);
  }
  const reader = stream.getReader();
  const output = new Uint8Array(entry.size);
  let written = 0;
  try {
    let finished = false;
    while (!finished) {
      checkAbort(signal);
      const { value, done } = await reader.read();
      finished = done;
      if (!done) {
        requireValue(
          written + value.length <= output.length,
          'ZIP entry exceeds its declared size'
        );
        output.set(value, written);
        written += value.length;
      }
    }
  } finally {
    await reader.cancel();
  }
  checkAbort(signal);
  requireValue(written === entry.size, 'ZIP entry length mismatch');
  requireValue(crc32(output) === entry.crc, 'ZIP entry checksum mismatch');
  return new File([output], entry.name.split('/').pop(), { type: 'application/octet-stream' });
}

export async function listZipScpFiles(archive, archivePath = archive.name) {
  requireValue(archive.size <= 2 * 1024 * 1024 * 1024, 'ZIP exceeds the 2 GiB archive limit');
  const tailStart = Math.max(0, archive.size - 65557);
  const tail = new DataView(await archive.slice(tailStart).arrayBuffer());
  let end = -1;
  for (let p = tail.byteLength - 22; p >= 0; p -= 1) {
    if (
      tail.getUint32(p, true) === 0x06054b50 &&
      p + 22 + tail.getUint16(p + 20, true) === tail.byteLength
    ) {
      end = p;
      break;
    }
  }
  requireValue(end >= 0, 'Invalid or truncated ZIP archive');
  requireValue(
    tail.getUint16(end + 4, true) === 0 && tail.getUint16(end + 6, true) === 0,
    'Multi-volume ZIP is not supported'
  );
  let count = tail.getUint16(end + 10, true);
  let size = tail.getUint32(end + 12, true);
  let offset = tail.getUint32(end + 16, true);
  let directoryEnd = tailStart + end;
  const hasZip64 = end >= 20 && tail.getUint32(end - 20, true) === 0x07064b50;
  if (hasZip64) {
    requireValue(
      tail.getUint32(end - 16, true) === 0 && tail.getUint32(end - 4, true) === 1,
      'Multi-volume ZIP64 is not supported'
    );
    const address = uint64(tail, end - 12);
    requireValue(address + 56 <= directoryEnd - 20, 'Invalid ZIP64 end record bounds');
    const record = new DataView(await archive.slice(address, address + 56).arrayBuffer());
    requireValue(record.getUint32(0, true) === 0x06064b50, 'Invalid ZIP64 end record');
    const recordSize = uint64(record, 4);
    requireValue(
      recordSize >= 44 && address + 12 + recordSize === directoryEnd - 20,
      'Invalid ZIP64 end record length'
    );
    requireValue(
      record.getUint32(16, true) === 0 && record.getUint32(20, true) === 0,
      'Multi-volume ZIP64 is not supported'
    );
    count = uint64(record, 32);
    size = uint64(record, 40);
    offset = uint64(record, 48);
    requireValue(uint64(record, 24) === count, 'ZIP64 entry counts disagree');
    directoryEnd = address;
  } else {
    requireValue(
      count !== 65535 && size !== 0xffffffff && offset !== 0xffffffff,
      'Missing ZIP64 end record'
    );
    requireValue(tail.getUint16(end + 8, true) === count, 'ZIP entry counts disagree');
  }
  // Discovery is independent of the 10,000-file backend parsing budget.
  requireValue(size <= 64 * 1024 * 1024, 'ZIP directory index exceeds 64 MiB');
  requireValue(count * 46 <= size, 'ZIP entry count exceeds directory bounds');
  requireValue(offset + size === directoryEnd, 'Invalid ZIP directory bounds');
  const bytes = new Uint8Array(await archive.slice(offset, offset + size).arrayBuffer());
  const directory = new DataView(bytes.buffer);
  const files = [];
  let skipped = 0;
  let p = 0;
  for (let i = 0; i < count; i += 1) {
    requireValue(
      p + 46 <= size && directory.getUint32(p, true) === 0x02014b50,
      'Invalid ZIP directory entry'
    );
    const nameLength = directory.getUint16(p + 28, true);
    const length =
      46 + nameLength + directory.getUint16(p + 30, true) + directory.getUint16(p + 32, true);
    requireValue(p + length <= size, 'Truncated ZIP directory entry');
    const name = new TextDecoder()
      .decode(bytes.subarray(p + 46, p + 46 + nameLength))
      .replace(/\\/g, '/');
    requireValue(
      !name.startsWith('/') &&
        !name.split('/').includes('..') &&
        !name.includes(String.fromCharCode(0)) &&
        !/^[A-Za-z]:/.test(name),
      'Unsafe path in ZIP archive'
    );
    if (/\.scp$/i.test(name)) {
      const entry = {
        name,
        flags: directory.getUint16(p + 8, true),
        method: directory.getUint16(p + 10, true),
        crc: directory.getUint32(p + 16, true),
        compressedSize: directory.getUint32(p + 20, true),
        size: directory.getUint32(p + 24, true),
        offset: directory.getUint32(p + 42, true),
        directoryOffset: offset,
      };
      let disk = directory.getUint16(p + 34, true);
      if (
        entry.size === 0xffffffff ||
        entry.compressedSize === 0xffffffff ||
        entry.offset === 0xffffffff ||
        disk === 65535
      ) {
        const extraEnd = p + 46 + nameLength + directory.getUint16(p + 30, true);
        let found = false;
        for (let extra = p + 46 + nameLength; extra + 4 <= extraEnd;) {
          const tag = directory.getUint16(extra, true);
          const extraSize = directory.getUint16(extra + 2, true);
          requireValue(extra + 4 + extraSize <= extraEnd, 'Truncated ZIP extra field');
          if (tag === 1) {
            let valueOffset = extra + 4;
            const next64 = () => {
              requireValue(valueOffset + 8 <= extra + 4 + extraSize, 'Truncated ZIP64 extra field');
              const value = uint64(directory, valueOffset);
              valueOffset += 8;
              return value;
            };
            if (entry.size === 0xffffffff) entry.size = next64();
            if (entry.compressedSize === 0xffffffff) entry.compressedSize = next64();
            if (entry.offset === 0xffffffff) entry.offset = next64();
            if (disk === 65535) {
              requireValue(valueOffset + 4 <= extra + 4 + extraSize, 'Truncated ZIP64 disk field');
              disk = directory.getUint32(valueOffset, true);
            }
            found = true;
            break;
          }
          extra += 4 + extraSize;
        }
        requireValue(found, 'Missing ZIP64 entry metadata');
      }
      requireValue(disk === 0, 'Multi-volume ZIP entry is not supported');
      files.push({
        file: { name: name.split('/').pop(), size: entry.size },
        relativePath: `${archivePath}/${name}`,
        loadFile: (signal) => extractEntry(archive, entry, signal),
      });
    } else if (!name.endsWith('/')) skipped += 1;
    p += length;
  }
  requireValue(p === size, 'Unexpected ZIP directory data');
  return { files, skipped };
}
